<template>
  <div class="comment_footer">
    <div class="textfield_container extrainfoDv attachment">
      <a class="comment_footer_icon" @click="uploadFile()">
        <input
          :class="'commentUploader' + sourceId"
          @change="uploadMedia($event)"
          class="d-none"
          type="file"
          name="file"
          id="uploadImage"
          ref="file"
        />
        <IconComponent path="" className="" icon="Camera.svg" />
      </a>
    </div>
    <div
      class="textfield_container extrainfoDv textfield"
      :class="{ limitExceeded: isLimitExceeded }"
    >
      <div class="d-flex align-items-end w-100 firstDv">
        <ProfileAvatarComponent
          v-if="isComment"
          :badges="false"
          :border="true"
          class="small mr-10 c-pointer"
          @click="goToProfile(user)"
          :profileImage="profilePicture"
        />
        <div
          class="textarea"
          :class="'commentTextField' + sourceId"
          @input="debounce($event)"
          @paste="onPaste($event)"
          @keyup="onBackspacePressed($event)"
          @keypress.enter="onEnterPressed($event)"
          contenteditable
          :data-placeholder="placehorderText"
        ></div>
        <template
          v-if="comment.suggestions.list && comment.suggestions.list.length"
        >
          <div
            class="suggestions bottom"
            v-if="comment.suggestions.symbol == '$'"
          >
            <StockAutocompleteComponent
              v-for="stock in comment.suggestions.list"
              :key="stock"
              @click="addStockToComment(stock)"
              :stock="stock"
            />
          </div>
          <div
            class="suggestions bottom"
            v-if="comment.suggestions.symbol == '@'"
          >
            <ProfileAutocompleteComponent
              v-for="user in comment.suggestions.list"
              :key="user._id"
              @click="addUserToComment(user)"
              :user="user"
            />
          </div>
        </template>
        <template v-if="isLoading">
          <div class="loader"></div>
        </template>
        <template v-else>
          <a class="comment_footer_icon emoji_icon">
            <IconComponent
              path=""
              @click.stop="toggleEmojiPicker()"
              className="createpost_footer_icon"
              icon="Post_Smiley.svg"
            />
            <div class="emoji_picker_container">
              <div
                class="emoji_backdrop"
                v-if="isEmojiPickerShown"
                @click.stop="toggleEmojiPicker(false)"
              ></div>
              <VuemojiPicker
                class="emojiPicker"
                v-if="isEmojiPickerShown"
                @emojiClick="selectEmoji"
                numColumns="auto"
                emojiSize="14px"
                emojiPadding="0"
                width="100%"
                :isDark="true"
                background="#201e29"
              />
            </div>
          </a>
          <a class="comment_footer_icon send_icon">
            <IconComponent
              path=""
              @click.stop="onEnterPressed($event, true)"
              className="createpost_footer_icon"
              icon="Send_Icon.svg"
            />
          </a>
        </template>
      </div>
      <div class="media_card" v-if="comment.media && comment.media.length">
        <template v-for="(media, index) in comment.media" :key="index">
          <i
            @click="removeMedia(index)"
            class="fas fa-times-circle icon_cross clickable"
          ></i>
          <img :src="media.url" v-if="!media.type.includes('video')" />
          <video
            controls
            style="height: 100%; width: 100%"
            :src="media.url"
            v-else
          ></video>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useComment } from "../../composables/comments.composable";
import { useUserInterface } from "../../composables/userinterface.composable";
import GeneralUserModel from "../../models/generaluser.model";
import firebaseServices from "@/services/firebase.service";
import storageService from "@/services/storage.service";
import utilService from "@/services/util.service";
import IconComponent from "@/components/IconComponent.vue";
import { computed, defineComponent, onMounted, ref } from "vue";
import { EmojiClickEventDetail, VuemojiPicker } from "vuemoji-picker";
import ProfileAutocompleteComponent from "../autocomplete-components/ProfileAutocompleteComponent.vue";
import StockAutocompleteComponent from "../autocomplete-components/StockAutocompleteComponent.vue";
import { TYPE } from "vue-toastification";
import ProfileAvatarComponent from "@/components/ProfileAvatarComponent.vue";
import router from "@/router/router";
import { RouteEnum } from "@/router/routeNames";
import { usePosts } from "../../composables/posts.composables";
import { useAppI18n } from "@/composables/useI18n";

export default defineComponent({
  props: ["isComment", "sourceId", "sourceType", "postId", "userComment"],
  components: {
    StockAutocompleteComponent,
    ProfileAutocompleteComponent,
    VuemojiPicker,
    ProfileAvatarComponent,
    IconComponent,
  },
  setup(props, context) {
    const { t } = useAppI18n();

    const placehorderText = computed(() => {
      return props.isComment
        ? t("common.actions.type_comment")
        : t("common.actions.type_replay");
    });
    
    const sourceId = computed(() => {
      return props.sourceId;
    });
    
    const postId = computed(() => {
      return props.postId;
    });

    const isComment = ref<Boolean>(props.isComment ?? true);

    const sourceType = computed(() => {
      if (isComment.value) {
        return props.sourceType;
      } else {
        return "comment";
      }
    });

    const user = ref<any>(storageService.user);

    onMounted(() => {
      if (!user) {
        setTimeout(() => {
          user.value = storageService.user;
        }, 500);
      }
    });

    const isEmojiPickerShown = ref<Boolean>(false);
    
    const profilePicture = computed(() => {
      if (user && user.value && user.value._id) {
        var prof = utilService.getUserProfilePicture(
          user.value._id!,
          user.value.profilePicture!,
          user.value.gender!
        );
        return prof;
      }
      return "undefined";
    });
    
    const {
      onPaste,
      debounceOnInput,
      onBackspaceKey,
      addStockTag,
      checkForLinks,
      getTextarea,
      addUserTag,
    } = useUserInterface();

    const { comment, addComment, isLoading } = useComment();

    const getImageUrl = (url: string, directory: string | null) =>
      utilService.getImageUrl(url, directory);

    const checkForMeta = (ev: any) => checkForLinks(ev, comment.value);

    const debounce = (ev: any) => debounceOnInput(() => checkForMeta(ev));

    const addStockToComment = (stock: any) =>
      addStockTag(stock, comment.value, "commentTextField" + sourceId.value);

    const addUserToComment = (user: GeneralUserModel) =>
      addUserTag(user, comment.value, "commentTextField" + sourceId.value);

    const onBackspacePressed = (ev: any) => {
      validateLimit(ev);
      onBackspaceKey(ev, comment.value);
    };

    const getUserProfilePicture = (user: GeneralUserModel) =>
      utilService.getUserProfilePicture(
        user._id!,
        user.profilePicture!,
        user.gender!
      );

    const uploadFile = () => {
      var elems: any = document.getElementsByClassName(
        "commentUploader" + props.sourceId
      );
      if (elems && elems.length) {
        elems[elems.length - 1].click();
      }
    };

    const uploadMedia = async (e: any) => {
      isLoading.value = true;
      let file = e.target.files[0];
      if (file) {
        var isFileValid =
          file.type.includes("image") || file.type.includes("video");
        if (isFileValid) {
          let result = await firebaseServices.uploadMedia(file);
          let data = {
            _id: new Date().toString(),
            type: file.type,
            url: result,
          };
          comment.value.media = [];
          comment.value.media!.push(data);
        } else {
          utilService.showToast("Alert! Invalid File Uploaded", TYPE.ERROR);
        }
      }
      isLoading.value = false;
      e.target.value = "";
    };

    const removeMedia = (index: number) => {
      comment.value.media?.splice(index, 1);
    };

    const toggleEmojiPicker = (val: boolean | null) => {
      if (val == null) {
        isEmojiPickerShown.value = !isEmojiPickerShown.value;
      } else {
        isEmojiPickerShown.value = val;
      }
    };

    const selectEmoji = (detail: EmojiClickEventDetail) => {
      getTextarea(
        "append",
        detail.unicode,
        comment.value,
        "commentTextField" + props.sourceId
      );
    };

    const goToProfile = (user: any) => {
      router.push({
        name: RouteEnum.profile,
        params: { username: user.userName! },
      });
    };

    const isLimitExceeded = ref<boolean>(false);
  
    const validateLimit = (event: KeyboardEvent) => {
      const targetElement = event.target as HTMLDivElement;
      if (targetElement) {
        const tempElement = document.createElement("div");
        tempElement.innerHTML = targetElement.innerHTML.trim();
        const textLength = tempElement.textContent?.length || 0;
        isLimitExceeded.value = textLength > 8000;
      }
    };

    const onEnterPressed = (ev: KeyboardEvent, onSend: boolean = false) => {
      if (isLimitExceeded.value) return;
      addComment(
        ev,
        comment.value,
        sourceId.value,
        sourceType.value,
        postId.value,
        onSend
      );
      context.emit("handleReplyInput");
    };

    return {
      goToProfile,
      isLoading,
      comment,
      isEmojiPickerShown,
      isComment,
      sourceType,
      profilePicture,
      sourceId,
      postId,
      getImageUrl,
      onPaste,
      debounce,
      addComment,
      addStockToComment,
      getUserProfilePicture,
      user,
      addUserToComment,
      selectEmoji,
      onBackspacePressed,
      toggleEmojiPicker,
      uploadFile,
      uploadMedia,
      removeMedia,
      validateLimit,
      isLimitExceeded,
      onEnterPressed,
      placehorderText,
    };

  },
});
</script>

<style lang="scss">
.emoji_backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 11;
}

.comment_footer {
  display: flex;
  align-items: center;
  position: relative;
  &.isRepost {
    z-index: 99999;
    .textfield_container {
      margin-bottom: 6px;
    }
  }

  .profile_avatar {
    height: 40px;
    flex: 0 0 40px;
    margin-inline-end: 15px;
    align-self: baseline;
    cursor: pointer;
  }

  .textfield_container {
    // flex-flow: column;
    background: var(--base-lightest);
    border-radius: 25px;
    flex-grow: 1;
    padding: 4px 10px;
    display: flex;
    max-width: 100%;
    overflow: hidden;
    align-items: center;
    margin-bottom: 20px;
    min-height: 45px;
    .textarea {
      padding-block: 9px;
    }
    &.textfield {
      flex-direction: column;
    }
    &.attachment {
      min-width: 45px;
      padding: 0;
      flex-grow: unset;
      margin-inline-end: 8px !important;
      .comment_footer_icon {
        margin: 0 auto;
      }
    }
    .profile_container {
      top: 3px;
    }

    &.limitExceeded {
      box-shadow: 0 0 0 1px rgba(255, 0, 0, 0.5);
    }

    > .suggestions {
      top: unset;
      bottom: 75%;
    }

    .textarea {
      width: 100%;
      flex-grow: 1;
      font-size: var(--font-sm);
      font-weight: 400;
      color: var(--text-color);
      text-align: left;
      outline: none;
      max-width: calc(100% - 30px - 5px);
      overflow: hidden auto;
      max-height: 110px;
      @include scrollbar();
      padding-inline-end: 5px;

      a {
        color: var(--anchor-tag);
        font-size: var(--font-md);
        font-weight: 400;
        text-decoration: underline;
      }

      &:empty:before {
        content: attr(data-placeholder);
        color: var(--base-text-white);
        opacity: 0.6;
      }

      br {
        display: none;
      }

      * {
        display: inline;
        white-space: nowrap;
      }
    }

    button {
      color: #fb267b;
      font-weight: 500;
      font-size: var(--font-md);
      background: none;
      outline: none;
      border: none;
      margin-inline-start: 5px;
    }

    .media_card {
      position: relative;
      width: 100%;
      border-radius: 10px;
      height: 180px;
      overflow: hidden;
      margin-top: 10px;
      text-align: center;

      .icon_cross {
        position: absolute;
        top: 5px;
        right: 5px;
        color: var(--text-color);
        font-size: 1.2rem;
        z-index: 9999;
      }

      img {
        height: 100%;
        width: auto;
      }

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: #0003;
      }
    }
  }

  .img_uploader {
    margin-inline-start: auto;
    color: var(--text-color);
    opacity: 0.5;
    cursor: pointer;
  }

  .comment_footer_icon {
    cursor: pointer;
    margin-inline-start: auto;
    margin-inline-end: 10px;
    display: flex;
    color: var(--base-text);
    .svg_icon {
      width: 19px;
      height: 19px;
      color: var(--base-text);
    }
    &.send_icon {
      margin-inline-end: 0;
    }

    emoji-picker {
      position: absolute;
      z-index: 99999;
      left: 0;
      bottom: 100%;
    }
  }
}

[dir="rtl"] {
  .comment_footer {
    .textfield_container {
      .textarea {
        text-align: right;
      }
    }
  }
}

.cross--icon {
  position: absolute;
  right: 0;
}
.extrainfoDv {
  .firstDv {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  .comment_footer_icon {
    color: var(--base-text);
    span {
      display: flex;
      align-items: center;
    }
  }
}
</style>
